import { useEffect, useState } from "react";
import { api } from "../../api/client";
import {
  Button,
  DataCard,
  Icon,
  Input,
  PageTitle,
  Spinner,
  Td,
  Th
} from "../../components/ui";

export default function SettingsPage({ setToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);

    try {
      const res = await api.getSystemSettings();

      if (res.success) {
        setData(res);
      } else {
        setToast({
          type: "error",
          message: res.message || "Failed to load settings"
        });
      }
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Failed to load settings"
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function update(sheetName, row, col, val) {
    try {
      const res = await api.updateSystemSetting({
        sheetName,
        row,
        col,
        val
      });

      if (!res.success) {
        setToast({
          type: "error",
          message: res.message || "Save failed"
        });
      }
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Save failed"
      });
    }
  }

  async function add(sheetName, rowData) {
    try {
      const res = await api.addSystemSettingRow({
        sheetName,
        rowData
      });

      setToast({
        type: res.success ? "success" : "error",
        message: res.success ? "Saved" : res.message
      });

      if (res.success) load();
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Save failed"
      });
    }
  }

  if (loading) return <Spinner />;

  return (
    <>
      <PageTitle
        title="Data Settings"
        subtitle="Manage salary ranges, incentives, deductions, and staff session rates."
      />

      <SettingsTable
        title="Base Salary Ranges"
        rows={data?.salaryRanges || []}
        cols={[
          ["position", "Position", 1],
          ["t1", "1-5 Students", 2],
          ["t2", "6-7 Students", 3],
          ["t3", "8-10 Students", 4],
          ["t4", "11+ Students", 5]
        ]}
        sheet="Salary Range"
        onUpdate={update}
        onAdd={add}
      />

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <SimpleCategoryTable
          title="Additional Incentives"
          sheet="Additional Incentives"
          rows={data?.incentives || []}
          onUpdate={update}
          onAdd={add}
        />

        <SimpleCategoryTable
          title="Standard Deductions"
          sheet="Deduction"
          rows={data?.deductions || []}
          onUpdate={update}
          onAdd={add}
        />
      </div>

      <StaffRatesTable setToast={setToast} />
    </>
  );
}

// ── Tarif Staff per sesi ─────────────────────────────────────────
//
// Katalog terpisah dari Base Salary Ranges di atas, dan memang harus begitu: yang di
// atas bracket berdasarkan JUMLAH MURID (milik trainer), yang ini tarif per SESI KERJA
// (milik staff). Satu tabel untuk dua aturan berbeda akan membuat separuh kolomnya
// kosong di separuh barisnya.
//
// Tarif boleh berbeda tiap sesi — sesi malam tak selalu dibayar sama dengan pagi.
function StaffRatesTable({ setToast }) {
  const [sessions, setSessions] = useState([]);
  const [rows, setRows] = useState([]);
  const [tanpaTarif, setTanpaTarif] = useState([]);
  const [jabatanBaru, setJabatanBaru] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await api.getStaffRates();
      if (res?.success) {
        setSessions(res.sessions || []);
        setRows(res.rows || []);
        setTanpaTarif(res.tanpaTarif || []);
      } else {
        setToast({ type: "error", message: res?.message || "Gagal memuat tarif staff" });
      }
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal memuat tarif staff" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function simpanTarif(position, session, nilai) {
    // Angka saja. Admin terbiasa mengetik "75.000" — titik & spasi dibuang supaya
    // tarifnya tak berubah diam-diam jadi 75 rupiah.
    const rate = Number(String(nilai).replace(/[^\d]/g, "")) || 0;
    try {
      const res = await api.setStaffRate({ position, session, rate });
      if (res?.success === false) {
        setToast({ type: "error", message: res.message });
        load();                        // tampilan dikembalikan ke keadaan server
        return;
      }
      setRows((lama) =>
        lama.map((r) =>
          r.position === position ? { ...r, rates: { ...r.rates, [session]: rate } } : r
        )
      );
      setTanpaTarif((lama) => (rate > 0 ? lama.filter((p) => p !== position) : lama));
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal menyimpan tarif" });
      load();
    }
  }

  async function tambahJabatan() {
    const nama = jabatanBaru.trim();
    if (!nama) return;
    try {
      const res = await api.addStaffPosition(nama);
      setToast({
        type: res?.success === false ? "error" : "success",
        message: res?.message || "Jabatan ditambahkan."
      });
      if (res?.success !== false) setJabatanBaru("");
      load();
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal menambah jabatan" });
    }
  }

  async function hapusJabatan(position) {
    if (!window.confirm(`Hapus jabatan "${position}" beserta kelima tarifnya?`)) return;
    try {
      const res = await api.deleteStaffPosition(position);
      setToast({
        type: res?.success === false ? "error" : "success",
        message: res?.message || "Jabatan dihapus."
      });
      load();
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal menghapus jabatan" });
    }
  }

  return (
    <DataCard className="mt-6">
      <div className="border-b border-slate-200 p-5 dark:border-slate-800">
        <div className="font-black">Tarif Staff (per sesi)</div>
        <p className="mt-1 text-xs text-slate-500">
          Untuk staff non-trainer. Gaji dihitung dari sesi yang dicentang admin di halaman
          Staff Payroll dikali tarif di bawah. Berlaku per cabang — pastikan cabang di
          pojok atas sudah benar.
        </p>
      </div>

      {tanpaTarif.length > 0 && (
        <div className="border-b border-amber-300/40 bg-amber-500/10 px-5 py-3 text-xs font-bold text-amber-700 dark:text-amber-400">
          Sudah ada staff berjabatan {tanpaTarif.join(", ")}, tapi tarifnya masih 0 —
          gajinya akan terhitung nol sampai diisi.
        </div>
      )}

      {loading ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] whitespace-nowrap text-left">
            <thead>
              <tr>
                <Th>Jabatan</Th>
                {sessions.map((s) => (
                  <Th key={s} center>
                    {s}
                  </Th>
                ))}
                <Th right>Hapus</Th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.branchId}-${row.position}`}
                  className="border-t border-slate-200 dark:border-slate-800"
                >
                  <Td>
                    <span className="font-bold">{row.position}</span>
                    {row.ditunjuk && (
                      <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-black text-primary">
                        dipakai
                      </span>
                    )}
                  </Td>
                  {sessions.map((s) => (
                    <Td key={s} center>
                      <Input
                        defaultValue={row.rates[s] || 0}
                        onBlur={(e) => simpanTarif(row.position, s, e.target.value)}
                      />
                    </Td>
                  ))}
                  <Td right>
                    <button
                      onClick={() => hapusJabatan(row.position)}
                      title={`Hapus jabatan ${row.position}`}
                      className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-red-500 dark:hover:bg-slate-700"
                    >
                      <Icon name="delete" className="text-base" />
                    </button>
                  </Td>
                </tr>
              ))}

              {!rows.length && (
                <tr className="border-t border-slate-200 dark:border-slate-800">
                  <Td colSpan={sessions.length + 2}>
                    <span className="text-sm text-slate-500">
                      Belum ada jabatan staff di cabang ini.
                    </span>
                  </Td>
                </tr>
              )}

              <tr className="border-t border-primary/20 bg-primary/5">
                <Td>
                  <Input
                    placeholder="Jabatan baru"
                    value={jabatanBaru}
                    onChange={(e) => setJabatanBaru(e.target.value)}
                  />
                </Td>
                <Td colSpan={sessions.length}>
                  <span className="text-xs text-slate-500">
                    Kelima sesinya dibuat sekaligus dengan tarif 0, tinggal diisi.
                  </span>
                </Td>
                <Td right>
                  <Button onClick={tambahJabatan}>
                    <Icon name="add" />
                  </Button>
                </Td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </DataCard>
  );
}

function SettingsTable({ title, rows, cols, sheet, onUpdate, onAdd }) {
  const [newRow, setNewRow] = useState({});

  return (
    <DataCard className="mb-6">
      <div className="border-b border-slate-200 p-5 font-black dark:border-slate-800">
        {title}
      </div>

      <table className="w-full min-w-[480px] whitespace-nowrap text-left">
        <thead>
          <tr>
            {cols.map((col) => (
              <Th key={col[0]}>{col[1]}</Th>
            ))}
            <Th right>Add</Th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr
              key={row.rowNumber}
              className="border-t border-slate-200 dark:border-slate-800"
            >
              {cols.map((col) => (
                <Td key={col[0]}>
                  <Input
                    defaultValue={row[col[0]]}
                    onBlur={(e) =>
                      onUpdate(sheet, row.rowNumber, col[2], e.target.value)
                    }
                  />
                </Td>
              ))}
              <Td />
            </tr>
          ))}

          <tr className="border-t border-primary/20 bg-primary/5">
            {cols.map((col) => (
              <Td key={col[0]}>
                <Input
                  placeholder={col[1]}
                  value={newRow[col[0]] || ""}
                  onChange={(e) =>
                    setNewRow({
                      ...newRow,
                      [col[0]]: e.target.value
                    })
                  }
                />
              </Td>
            ))}

            <Td right>
              <Button
                onClick={() =>
                  onAdd(
                    sheet,
                    cols.map((col) => newRow[col[0]] || "")
                  )
                }
              >
                <Icon name="add" />
              </Button>
            </Td>
          </tr>
        </tbody>
      </table>
    </DataCard>
  );
}

function SimpleCategoryTable({ title, sheet, rows, onUpdate, onAdd }) {
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");

  return (
    <DataCard>
      <div className="border-b border-slate-200 p-5 font-black dark:border-slate-800">
        {title}
      </div>

      <table className="w-full min-w-[480px] whitespace-nowrap text-left">
        <thead>
          <tr>
            <Th>Category</Th>
            <Th>Amount</Th>
            <Th right>Add</Th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr
              key={row.rowNumber}
              className="border-t border-slate-200 dark:border-slate-800"
            >
              <Td>
                <Input
                  defaultValue={row.category}
                  onBlur={(e) =>
                    onUpdate(sheet, row.rowNumber, 1, e.target.value)
                  }
                />
              </Td>

              <Td>
                <Input
                  type="number"
                  defaultValue={row.amount}
                  onBlur={(e) =>
                    onUpdate(sheet, row.rowNumber, 2, e.target.value)
                  }
                />
              </Td>

              <Td />
            </tr>
          ))}

          <tr className="border-t border-primary/20 bg-primary/5">
            <Td>
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="New category"
              />
            </Td>

            <Td>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Amount"
              />
            </Td>

            <Td right>
              <Button
                onClick={() => {
                  onAdd(sheet, [category, amount || 0]);
                  setCategory("");
                  setAmount("");
                }}
              >
                <Icon name="add" />
              </Button>
            </Td>
          </tr>
        </tbody>
      </table>
    </DataCard>
  );
}